import * as Sentry from '@sentry/browser'
import { kAgentV2RouterApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getResponseDebugContext, getTopLevelKeys } from '@/util/api/response-body'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { AgentV2ConversationDetailResponse, AgentV2ConversationDetailResponseSchema } from '@/agent-v2/store/schemas'
import { agentV2Request } from './agent-v2-request'
import { ensureOutlookPrivacyMode, isOutlookPrivacyMode } from '@/analytics/privacy/outlook-continuation-privacy'
import { getAgentV2ConversationSurface } from './get-agent-v2-conversation-surface'

export async function getAgentV2ConversationDetail(
  conversationId: string,
  signal?: AbortSignal,
  options?: { expectedNotFound?: boolean; paginated?: boolean; cursor?: string }
): Promise<AgentV2ConversationDetailResponse> {
  // Check before fetching: tracker code can observe network responses as well as rendered DOM.
  // The add-in uses its own bootstrap and its separate after-Copy analytics boundary.
  if (window.__paxtonWebDocument && !isOutlookPrivacyMode()) {
    const origin = await getAgentV2ConversationSurface(conversationId, signal)
    if (signal?.aborted) throw new DOMException('Request aborted', 'AbortError')
    if (!ensureOutlookPrivacyMode(origin)) throw new DOMException('Opening protected conversation', 'AbortError')
  }
  const query = new URLSearchParams()
  if (options?.paginated) query.set('paginated', 'true')
  if (options?.cursor) query.set('cursor', options.cursor)
  const url = `${kPaxtonAppApiRESTBaseUrl()}${kAgentV2RouterApiBasePath}/conversations/${conversationId}${query.size ? `?${query}` : ''}`
  return agentV2Request({
    url,
    method: 'GET',
    signal,
    expectedErrorStatuses: options?.expectedNotFound ? [404] : undefined,
    sentryEndpoint: 'GET /api/v2/agent/conversations/{conversation_id}',
    parseResponse: (data, { response, sentryTags }) => {
      const parsed = AgentV2ConversationDetailResponseSchema.safeParse(data)
      if (parsed.success) return parsed.data

      Sentry.withScope((scope) => {
        scope.setTags(sentryTags)
        Sentry.captureException(new Error('Agent v2 conversation detail response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            conversationId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid Agent v2 conversation detail response shape', parsed.error.issues)
    },
  })
}
